import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import { track } from '../../utils/analytics';
import { planName } from '../../utils/planNames';

const API_URL = process.env.REACT_APP_API_URL;

const PRICES = {
  monthly: { label: 'Monthly', price: '£59', per: 'per month' },
  yearly: { label: 'Yearly', price: '£588', per: 'per year, £49 a month' },
};

const FREE_INCLUDES = [
  { text: 'The AI builder and unlimited drafts', included: true },
  { text: 'One live flow on one WhatsApp number, in one workspace', included: true },
  { text: 'Unlimited submissions, Submissions page and CSV export', included: true },
  { text: '"Built with FlowGate" on your flow', included: false },
  { text: 'Email, webhook and Google Sheets delivery', included: false },
  { text: 'Automatic triggers and live booking', included: false },
];

const PRO_INCLUDES = [
  'Unlimited live forms and triggers on one number',
  'As many environments and workspaces as you need',
  'Answers delivered to email, a webhook or Google Sheets',
  'Live booking inside the form, in your own schedule',
  'The "Built with FlowGate" line switched off',
  'No charge per user or per submission',
];

/**
 * The plan this organisation is on, and the one button that changes it.
 *
 * Both buttons hand off to Stripe-hosted pages rather than collecting anything here: card
 * details, invoices, VAT and cancellation are Stripe's problem, and every field we do not
 * render is a field we cannot leak. When the deployment has no Stripe keys the tab says so
 * plainly instead of showing a button that would 503.
 */
const BillingSettings = () => {
  const { authenticatedFetch } = useAuth();
  const { plan, limits, billing, brandingRequired, loading, error, refresh } = useEntitlements();
  const [interval, setInterval] = useState('monthly');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);

  const isPro = plan !== 'FREE';

  const go = async (path, body) => {
    setBusy(true);
    setActionError(null);
    try {
      const response = await authenticatedFetch(`${API_URL}${path}`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.code === 'BILLING_NOT_CONFIGURED'
          ? 'Billing is not set up on this deployment yet.'
          : payload.message || `Could not reach Stripe (${response.status})`);
      }
      window.location.href = payload.url;
    } catch (err) {
      setActionError(err.message);
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Plan and billing</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        What this organisation can do, and how to change it.
      </Typography>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
      {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle1">Current plan</Typography>
            <Chip label={planName(plan)} color={isPro ? 'primary' : 'default'} size="small" />
            {billing?.cancelAtPeriodEnd && (
              <Chip label="Cancels at period end" color="warning" size="small" variant="outlined" />
            )}
            {billing?.status === 'past_due' && (
              <Chip label="Payment failed" color="error" size="small" variant="outlined" />
            )}
          </Stack>

          <Typography variant="body2" color="text.secondary">
            {isPro
              ? 'Unlimited live forms and triggers, deliveries, live booking, and no FlowGate line on your forms.'
              : `${limits.liveFlows ?? 'Unlimited'} live flow on ${limits.numbers ?? 'unlimited'} WhatsApp number, `
                + 'unlimited submissions, and a small "Built with FlowGate" line on the last screen.'}
          </Typography>

          {billing?.currentPeriodEnd && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {billing.cancelAtPeriodEnd ? 'Access until ' : 'Renews on '}
              {new Date(billing.currentPeriodEnd * 1000).toLocaleDateString()}
            </Typography>
          )}

          {!isPro && (
            <List dense sx={{ mt: 1 }}>
              {FREE_INCLUDES.map((item) => (
                <ListItem key={item.text} disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {item.included
                      ? <CheckIcon fontSize="small" color="success" />
                      : <CloseIcon fontSize="small" color="disabled" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: item.included ? 'text.primary' : 'text.secondary',
                    }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {!billing?.configured && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Billing is not set up on this deployment, so everyone is on the Free plan. Nothing else
          is affected, flows, submissions and deliveries all work as normal.
        </Alert>
      )}

      {!isPro && billing?.configured && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>Upgrade to Team</Typography>

            <ToggleButtonGroup
              exclusive
              size="small"
              value={interval}
              onChange={(event, next) => next && setInterval(next)}
              sx={{ mb: 2 }}
            >
              {Object.entries(PRICES).map(([key, value]) => (
                <ToggleButton key={key} value={key} sx={{ textTransform: 'none' }}>
                  {value.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography variant="h5">{PRICES[interval].price}</Typography>
              <Typography variant="body2" color="text.secondary">{PRICES[interval].per}</Typography>
            </Stack>

            <List dense sx={{ mt: 1 }}>
              {PRO_INCLUDES.map((item) => (
                <ListItem key={item} disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 2 }} />

            <Button
              variant="contained"
              disabled={busy}
              onClick={() => {
                // Launch funnel event (docs/launch/ws-I.md), before the redirect fires.
                track('upgrade_clicked', { interval });
                go('/api/billing/checkout', { yearly: interval === 'yearly' });
              }}
            >
              {busy ? 'Opening Stripe…' : 'Upgrade to Team'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Payment is handled by Stripe. Cancel any time; no lock-in and no per-submission fees.
            </Typography>
          </CardContent>
        </Card>
      )}

      {isPro && billing?.manageable && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>Manage billing</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Change your card, download invoices, or cancel, all in Stripe.
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" disabled={busy} onClick={() => go('/api/billing/portal')}>
                {busy ? 'Opening Stripe…' : 'Manage billing'}
              </Button>
              <Button onClick={refresh} disabled={busy}>Refresh</Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {brandingRequired && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
          Free flows carry a small "Built with FlowGate" line on their last screen. It is added
          when the flow is sent to WhatsApp, so it never appears in your builder, and it
          disappears from your next publish as soon as you upgrade.
        </Typography>
      )}
    </Box>
  );
};

export default BillingSettings;
